import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Check } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";
import { FAMILIES } from "../data/catalog";

/* Spec rows to compare across families. Value getter reads from a family object. */
const COMPARE_ROWS = [
  { label: "Motor class", get: (f) => f.motorClass },
  { label: "Cycle time", get: (f) => f.speedRange },
  { label: "Boom arm range", get: (f) => f.armRange },
  { label: "SKUs available", get: (f) => `${f.skus.length}` },
  { label: "Positioning", get: (f) => f.tagline },
  { label: "Cabinet options", get: (f) => "Classic · LED · Slim · Glass · Display" },
  { label: "BBC Trust platform", get: () => "yes", isCheck: true },
  { label: "BBC Connect remote ops", get: () => "yes", isCheck: true },
  { label: "AMC cycle-limit lock", get: () => "yes", isCheck: true },
  { label: "RS485 / PMS integration", get: () => "yes", isCheck: true },
  { label: "IP rating", get: () => "IP65" },
  { label: "Warranty", get: () => "24 months mechanical" },
];

const BEST_FOR = {
  prime: ["Residential societies", "Villas & gated colonies", "Small commercial gates"],
  elite: ["Corporate campuses", "Factory & warehouse gates", "Logistics hubs"],
  flash: ["Toll & FASTag lanes", "Parking exits", "High-frequency shift gates"],
  max: ["Toll plazas (wide span)", "Airside / port yards", "Industrial 8–12m gates"],
};

export const ProductsCompare = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useSeo({
    title: "Compare BBC Boom Barrier Families · PRIME · ELITE · FLASH · MAX",
    description:
      "Side-by-side spec comparison of all 4 BBC boom-barrier families — motor class, cycle time, arm range, cabinets and platform features. Pick the right family for your site.",
    canonical: "https://boombarriercompany.com/products/compare",
  });

  return (
    <div className="bg-[#0F1113] min-h-screen text-white">
      <PlatformShellHeader />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="products-compare">
        <nav
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] flex items-center gap-2 mb-8 flex-wrap"
          data-testid="compare-breadcrumb"
        >
          <Link to="/" className="hover:text-white flex items-center gap-1"><Home size={11} /> Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-white">Products</Link>
          <span>/</span>
          <span className="text-white">Compare</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <h1
            className="lg:col-span-8 font-display text-4xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[1.02]"
            data-testid="compare-heading"
          >
            Compare all
            <br />
            <span className="text-[#A1A1AA]">4 families.</span>
          </h1>
          <p className="lg:col-span-4 text-[#A1A1AA] text-base md:text-lg font-body">
            Same BBC Trust platform inside every barrier. Pick the family tuned for your traffic, span and duty cycle.
          </p>
        </div>

        {/* Family header row — sticky-ish name cards */}
        <div className="mt-14 grid md:grid-cols-4 gap-2" data-testid="compare-family-headers">
          {FAMILIES.map((f) => (
            <Link
              key={f.slug}
              to={`/products/${f.slug}`}
              data-testid={`compare-family-${f.slug}`}
              className="group relative border border-white/10 bg-[#16191C] p-6 hover:border-[#00FF85]/50 transition overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${f.accentColor}, transparent)` }} />
              <div className="flex items-baseline gap-2 flex-wrap">
                <h2 className="font-display text-white text-2xl md:text-3xl font-black tracking-tight">{f.name}</h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: f.accentColor }}>{f.wattage}</span>
              </div>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85] leading-relaxed">{f.tagline}</p>
              <div className="mt-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 group-hover:text-[#00FF85] transition">
                Open family <ArrowRight size={10} />
              </div>
            </Link>
          ))}
        </div>

        {/* Spec matrix */}
        <div className="mt-10 border border-white/10 overflow-x-auto" data-testid="compare-table">
          <table className="w-full text-left border-collapse min-w-[880px]">
            <thead>
              <tr className="border-b border-white/10 bg-[#13161A]">
                <th className="p-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#71717A] w-[220px]">
                  Specification
                </th>
                {FAMILIES.map((f) => (
                  <th key={f.slug} className="p-4 font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: f.accentColor }}>
                    {f.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row, ri) => (
                <tr
                  key={row.label}
                  data-testid={`compare-row-${ri}`}
                  className={ri % 2 === 0 ? "bg-[#16191C]" : "bg-[#181B1E]"}
                >
                  <td className="p-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 border-r border-white/5">
                    {row.label}
                  </td>
                  {FAMILIES.map((f) => (
                    <td key={f.slug} className="p-4 font-display text-white text-sm md:text-base border-r border-white/5 last:border-r-0">
                      {row.isCheck ? (
                        <span className="inline-flex items-center gap-2 text-[#00FF85]">
                          <Check size={16} />
                          <span className="font-mono text-xs uppercase tracking-[0.18em]">included</span>
                        </span>
                      ) : (
                        <span>{row.get(f)}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Best-for panels */}
        <div className="mt-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Best-fit deployments</div>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {FAMILIES.map((f) => (
              <div
                key={f.slug}
                data-testid={`compare-bestfor-${f.slug}`}
                className="border border-white/10 bg-[#16191C] p-6"
              >
                <div className="flex items-baseline gap-2">
                  <div className="font-display text-white text-xl font-black tracking-tight">{f.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: f.accentColor }}>{f.wattage}</div>
                </div>
                <ul className="mt-4 space-y-2">
                  {(BEST_FOR[f.slug] || []).map((u) => (
                    <li key={u} className="flex items-start gap-2 text-sm text-white/85 font-body">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.accentColor }} />
                      {u}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 border-t border-white/5 pt-10 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/products"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white"
            data-testid="compare-back"
          >
            ← All families
          </Link>
          <Link
            to="/quote"
            data-testid="compare-cta"
            className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-5 py-3 text-xs uppercase tracking-wider transition"
          >
            Talk to a sales engineer
            <ArrowRight size={14} />
          </Link>
        </div>
      </main>
      <PlatformShellFooter />
    </div>
  );
};
